import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getGatewayOffer, recordGatewayOfferOp, GatewayOfferResponse } from '@extension/wa-communication-layer/api/api-calls';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { gatewayOfferUrl, shouldShowGatewayOffer } from './gateway-offer-logic';

/**
 * #1227: offers Gateway after a send failed because the WhatsApp Web tab was closed. Text comes from i18n keys (all
 * locales), the server only decides. Both buttons share one style so "Not now" carries equal weight. Renders nothing unless the server
 * says show:true for the shown arm (flag off, holdout, daily cap and old-client cases all return show:false).
 */
export const GatewayOffer: React.FC = () => {
  const { t } = useTranslation(namespace);
  const [offer, setOffer] = useState<GatewayOfferResponse | null>(null);
  const [open, setOpen] = useState(false);
  const fetchedRef = useRef(false);
  const shownRecordedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    getGatewayOffer()
      .then((res) => {
        if (shouldShowGatewayOffer(res)) {
          setOffer(res);
          setOpen(true);
        }
      })
      .catch(() => { /* a failed check never blocks the app: no offer */ });
  }, []);

  useEffect(() => {
    if (!open || !offer || shownRecordedRef.current) return;
    shownRecordedRef.current = true;
    recordGatewayOfferOp('shown').catch(() => {});
  }, [open, offer]);

  if (!offer) return null;

  const onPrimary = (): void => {
    recordGatewayOfferOp('clicked').catch(() => {});
    setOpen(false);
    window.open(gatewayOfferUrl(), '_blank', 'noopener');
  };
  const onNotNow = (): void => {
    recordGatewayOfferOp('dismissed').catch(() => {});
    setOpen(false);
  };
  const equalButtonClass = 'min-w-[9rem] flex-1 h-auto whitespace-normal py-2';

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onNotNow(); }}>
      <DialogContent className="max-w-md" data-testid="gateway-offer">
        <DialogHeader>
          <DialogTitle className="pe-8">{t('common.gatewayOfferTitle')}</DialogTitle>
          <DialogDescription>{t('common.gatewayOfferBody')}</DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" className={equalButtonClass} onClick={onNotNow}>{t('common.gatewayOfferDismiss')}</Button>
          <Button variant="outline" className={equalButtonClass} onClick={onPrimary}>{t('common.gatewayOfferCta')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
